package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.budget.PayTermsChanges
import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.timeline.InvalidTimelineException
import com.evoapps.evoshift.domain.timeline.EmployeeTimelineUseCase
import com.evoapps.evoshift.domain.timeline.TimelineEdit
import com.evoapps.evoshift.domain.timeline.TimelineEntry
import com.evoapps.evoshift.domain.timeline.TimelineKind
import com.evoapps.evoshift.domain.timeline.TimelineLane
import com.evoapps.evoshift.domain.timeline.TimelineProblem
import com.evoapps.evoshift.domain.timeline.TimelineRules
import com.evoapps.evoshift.domain.timeline.TimelineView
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.saves.PendingSaves
import com.evoapps.evoshift.saves.SaveKind
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.widgets.parseIsoDate
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.timeline_problem_date_missing
import evoshift.composeapp.generated.resources.timeline_problem_end_before_shifts
import evoshift.composeapp.generated.resources.timeline_problem_end_before_start
import evoshift.composeapp.generated.resources.timeline_problem_hire_after_shifts
import evoshift.composeapp.generated.resources.timeline_problem_hire_has_shifts
import evoshift.composeapp.generated.resources.timeline_problem_no_pay_terms
import evoshift.composeapp.generated.resources.timeline_problem_note_empty
import evoshift.composeapp.generated.resources.timeline_problem_note_too_long
import evoshift.composeapp.generated.resources.timeline_problem_outside
import evoshift.composeapp.generated.resources.timeline_problem_overlap
import evoshift.composeapp.generated.resources.timeline_problem_required
import evoshift.composeapp.generated.resources.timeline_problem_too_many
import evoshift.composeapp.generated.resources.timeline_problem_contract_after_end
import evoshift.composeapp.generated.resources.timeline_problem_contract_before_hire
import evoshift.composeapp.generated.resources.timeline_problem_contract_same_day
import evoshift.composeapp.generated.resources.timeline_problem_end_before_hire
import evoshift.composeapp.generated.resources.timeline_problem_end_before_shift_on
import evoshift.composeapp.generated.resources.timeline_problem_hire_after_shift_on
import evoshift.composeapp.generated.resources.timeline_problem_pay_after_end
import evoshift.composeapp.generated.resources.timeline_problem_pay_before_hire
import evoshift.composeapp.generated.resources.timeline_problem_pay_same_day
import evoshift.composeapp.generated.resources.timeline_saved
import evoshift.composeapp.generated.resources.timeline_saved_recompute
import evoshift.composeapp.generated.resources.timeline_saved_recompute_start
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.RequestType
import com.evoapps.evoshift.domain.timeline.RequestLane
import com.evoapps.evoshift.domain.timeline.TimelineRequest
import com.evoapps.evoshift.domain.timeline.TimelineRequests
import com.evoapps.evoshift.domain.timeline.TimelineRequestsUseCase
import com.evoapps.evoshift.domain.usecase.requests.AbsenceDecision
import com.evoapps.evoshift.domain.usecase.requests.ResolveConflictsFailedException
import com.evoapps.evoshift.ui.user.NewRequest
import evoshift.composeapp.generated.resources.requests_resolve_failed
import evoshift.composeapp.generated.resources.timeline_request_approved
import evoshift.composeapp.generated.resources.timeline_request_rejected
import evoshift.composeapp.generated.resources.timeline_request_sent
import evoshift.composeapp.generated.resources.timeline_request_withdrawn

/**
 * SH-212: the add / change dialog's fields as typed. Dates are "YYYY-MM-DD" (EvoDateField); the
 * values depend on the [kind] (see [TimelineEntry]).
 */
data class TimelineDraft(
    /** The entry changed (null: a new one). */
    val editingId: String? = null,
    val kind: TimelineKind,
    val fact: PayrollFact? = null,
    val from: String = "",
    val until: String = "",
    val contractTypeId: String = SystemContractType.CIVIL_LAW_CONTRACT.name,
    val declaredHours: String = "",
    val isHourly: Boolean = true,
    val rate: String = "",
    val salary: String = "",
    val fte: String = "1.0",
    val text: String = "",
    /** The note being changed (kept: its id stays). */
    val noteId: String? = null
) {
    val isNew: Boolean get() = editingId == null

    private fun day(text: String): LocalDate? = text.trim().takeIf { it.isNotEmpty() }?.let(::parseIsoDate)

    private fun number(text: String): Double? = text.trim().replace(',', '.').takeIf { it.isNotEmpty() }?.toDoubleOrNull()

    /** A typed value that is not one (a date that is no day, a rate that is no number): the dialog cannot be saved. */
    val invalidInput: Boolean
        get() = (from.isNotBlank() && day(from) == null) || (until.isNotBlank() && day(until) == null) ||
            (kind == TimelineKind.PAY && (if (isHourly) number(rate)?.takeIf { it > 0 } == null else number(salary)?.takeIf { it > 0 } == null ||
                number(fte)?.takeIf { it > 0 && it <= 1.0 } == null)) ||
            (kind == TimelineKind.CONTRACT && declaredHours.isNotBlank() && declaredHours.trim().toIntOrNull()?.takeIf { it in 1..744 } == null) ||
            (kind == TimelineKind.TAX_RELIEF && fact == null)

    /** The entry the fields describe, or null while [invalidInput]. */
    fun toEntry(): TimelineEntry? {
        if (invalidInput) return null
        val start = day(from)
        val end = day(until)
        return when (kind) {
            TimelineKind.HIRE, TimelineKind.END -> TimelineEntry(kind, start)
            TimelineKind.CONTRACT -> TimelineEntry(kind, start, contractTypeId = contractTypeId, declaredHours = declaredHours.trim().toIntOrNull())
            TimelineKind.PAY -> TimelineEntry(
                kind, start,
                pay = if (isHourly) CompensationModel.Hourly(number(rate)!!, 0.0)
                else CompensationModel.MonthlyFixed(number(salary)!!, 0.0, number(fte)!!)
            )
            TimelineKind.STUDENT, TimelineKind.UNPAID, TimelineKind.OHS_TRAINING, TimelineKind.MEDICAL_EXAM -> TimelineEntry(kind, start, end)
            // SH-232: whole months (TimelineRules.snapped).
            TimelineKind.NOT_COUNTED -> TimelineRules.snapped(TimelineEntry(kind, start, end))
            TimelineKind.TAX_RELIEF -> TimelineEntry(kind, start, end, fact = fact)
            TimelineKind.NOTE -> TimelineEntry(kind, start, text = text.trim(), noteId = noteId)
        }
    }

    companion object {
        /** A new entry of [lane], from [today] (the end of a status, the validity: empty). */
        fun new(lane: TimelineLane, today: LocalDate): TimelineDraft =
            TimelineDraft(kind = lane.kind, fact = lane.fact, from = today.toString())

        /** The fields of [entry], to change it. */
        fun of(entry: TimelineEntry): TimelineDraft {
            val pay = entry.pay
            return TimelineDraft(
                editingId = entry.id,
                kind = entry.kind,
                fact = entry.fact,
                from = entry.from?.toString().orEmpty(),
                until = entry.until?.toString().orEmpty(),
                contractTypeId = entry.contractTypeId ?: SystemContractType.CIVIL_LAW_CONTRACT.name,
                declaredHours = entry.declaredHours?.toString().orEmpty(),
                isHourly = pay !is CompensationModel.MonthlyFixed,
                rate = (pay as? CompensationModel.Hourly)?.rate?.let(::plain).orEmpty(),
                salary = (pay as? CompensationModel.MonthlyFixed)?.baseSalary?.let(::plain).orEmpty(),
                fte = (pay as? CompensationModel.MonthlyFixed)?.fte?.let(::plain) ?: "1.0",
                text = entry.text.orEmpty(),
                noteId = entry.noteId
            )
        }

        private fun plain(value: Double): String = if (value % 1.0 == 0.0) value.toLong().toString() else value.toString()
    }
}

data class EmployeeTimelineUiState(
    /** Only until the first timeline arrives. */
    val isLoading: Boolean = true,
    val view: TimelineView? = null,
    /** The viewer may not see this timeline (or it could not be read). */
    val notAvailable: Boolean = false,
    /** The add / change dialog. */
    val draft: TimelineDraft? = null,
    /** Why the dialog's entry cannot be saved (shown in it). */
    val draftProblem: UiText? = null,
    /** The entry waiting for "Remove?" to be confirmed. */
    val removing: TimelineEntry? = null,
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val message: UiText? = null,
    /** SH-224: the person's requests in the visible days (null: not shown here). */
    val requests: TimelineRequests? = null,
    /** SH-224: SH-223's new-request form, opened from the timeline. */
    val newRequest: TimelineNewRequest? = null,
    /** SH-224: the request whose detail is open, and what waits on it. */
    val detail: TimelineRequestDetail? = null,
    /** SH-224: a request being sent, decided or withdrawn. */
    val requestSaving: Boolean = false
) {
    val canEdit: Boolean get() = view?.canEdit == true

    /** SH-224: owners and financial managers add a request from the timeline (SH-223's form). */
    val canAddRequests: Boolean get() = canEdit && requests != null

    /** SH-224: the viewer approves, rejects and withdraws the person's requests (SH-223's rules). */
    val canDecideRequests: Boolean get() = view?.canDecideRequests == true && requests != null

    /** SH-224: the request of the open detail (null once it is gone, withdrawn say). */
    val openRequest: TimelineRequest? get() = detail?.let { requests?.find(it.key) }
}

/** SH-224: the new-request form for the person: their facts as the form reads them, and the type it starts on. */
data class TimelineNewRequest(
    val form: AbsencesUiState,
    val initialType: RequestType? = null,
    /** SH-223: whom the form is filled in for ("Dla: …"; null: the viewer's own). */
    val forName: String? = null,
    /** SH-223: "Approve now" offered (the viewer decides requests). */
    val canApproveNow: Boolean = false
)

/** SH-224: an open request's detail: the rejection's reason asked, the withdrawal confirmed, an approval over a limit. */
data class TimelineRequestDetail(
    val key: String,
    val rejecting: Boolean = false,
    val withdrawing: Boolean = false,
    val overLimit: LimitCheck.Exceeds? = null
)

/** SH-224: the type the form starts on when opened from [lane]'s "+" (absences: the form's own first, annual leave). */
fun RequestLane.initialType(): RequestType? = when (this) {
    RequestLane.ABSENCES -> null
    RequestLane.REMOTE_AND_EVENTS -> RequestType.Work(WorkRequestKind.REMOTE_WORK)
    RequestLane.OVERTIME -> RequestType.Work(WorkRequestKind.OVERTIME)
}

/**
 * The text of a timeline problem. SH-234: each says what to do, with the days involved (the
 * entry's own and the one it collides with: the start or end of cooperation, a shift).
 */
fun TimelineProblem.toUiText(): UiText {
    fun day(value: kotlinx.datetime.LocalDate?): String = value?.let { com.evoapps.evoshift.ui.user.formatDay(it) }.orEmpty()
    val isPay = entryKind == TimelineKind.PAY
    return when (kind) {
        TimelineProblem.Kind.BEFORE_HIRE -> UiText.Resource(
            if (isPay) Res.string.timeline_problem_pay_before_hire else Res.string.timeline_problem_contract_before_hire, day(on), day(limit)
        )
        TimelineProblem.Kind.AFTER_END -> UiText.Resource(
            if (isPay) Res.string.timeline_problem_pay_after_end else Res.string.timeline_problem_contract_after_end, day(on), day(limit)
        )
        TimelineProblem.Kind.HIRE_AFTER_SHIFTS -> limit?.let { UiText.Resource(Res.string.timeline_problem_hire_after_shift_on, day(it)) }
            ?: kind.toUiText()
        TimelineProblem.Kind.END_BEFORE_SHIFTS -> limit?.let { UiText.Resource(Res.string.timeline_problem_end_before_shift_on, day(it)) }
            ?: kind.toUiText()
        TimelineProblem.Kind.END_BEFORE_START -> if (entryId == TimelineKind.END.name && limit != null) {
            UiText.Resource(Res.string.timeline_problem_end_before_hire, day(limit))
        } else kind.toUiText()
        TimelineProblem.Kind.OVERLAP -> when {
            entryKind == TimelineKind.PAY && on != null -> UiText.Resource(Res.string.timeline_problem_pay_same_day, day(on))
            entryKind == TimelineKind.CONTRACT && on != null -> UiText.Resource(Res.string.timeline_problem_contract_same_day, day(on))
            else -> kind.toUiText()
        }
        else -> kind.toUiText()
    }
}

/** The text of a timeline problem's [kind] alone (without its days). */
fun TimelineProblem.Kind.toUiText(): UiText = UiText.Resource(
    when (this) {
        TimelineProblem.Kind.DATE_MISSING -> Res.string.timeline_problem_date_missing
        TimelineProblem.Kind.END_BEFORE_START -> Res.string.timeline_problem_end_before_start
        TimelineProblem.Kind.OVERLAP -> Res.string.timeline_problem_overlap
        TimelineProblem.Kind.BEFORE_HIRE, TimelineProblem.Kind.AFTER_END -> Res.string.timeline_problem_outside
        TimelineProblem.Kind.REQUIRED -> Res.string.timeline_problem_required
        TimelineProblem.Kind.NO_PAY_TERMS -> Res.string.timeline_problem_no_pay_terms
        TimelineProblem.Kind.HIRE_HAS_SHIFTS -> Res.string.timeline_problem_hire_has_shifts
        TimelineProblem.Kind.HIRE_AFTER_SHIFTS -> Res.string.timeline_problem_hire_after_shifts
        TimelineProblem.Kind.END_BEFORE_SHIFTS -> Res.string.timeline_problem_end_before_shifts
        TimelineProblem.Kind.NOTE_EMPTY -> Res.string.timeline_problem_note_empty
        TimelineProblem.Kind.NOTE_TOO_LONG -> Res.string.timeline_problem_note_too_long
        TimelineProblem.Kind.TOO_MANY -> Res.string.timeline_problem_too_many
    }
)

/**
 * SH-212: one person's timeline at a workplace. The workplace's owner and financial managers
 * (from the employee form) add, change, move (drag on wide screens, dates everywhere) and remove
 * entries; the person (from the profile, [userId] null) reads their own. Saves are optimistic:
 * the timeline changes at once and goes back if the save fails; the open months a change touches
 * are recalculated in the background afterwards (the app's saves, not this screen's).
 */
class EmployeeTimelineViewModel(
    private val workplaceId: String,
    private val userId: String?,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val timelines: EmployeeTimelineUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val payTermsChanges: PayTermsChanges? = null,
    private val saves: PendingSaves = PendingSaves(),
    /** SH-224: the person's requests; without it the timeline shows none. */
    private val requests: TimelineRequestsUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(EmployeeTimelineUiState(requests = requests?.let { TimelineRequests() }))
    val uiState: StateFlow<EmployeeTimelineUiState> = _uiState.asStateFlow()
    private val job = JobSlot()
    private val requestsJob = JobSlot()
    private var viewer: User? = null
    /** SH-224: the days the requests were read for (read again when the axis changes). */
    private var loadedAxis: ClosedRange<LocalDate>? = null

    init {
        job.launch(viewModelScope) {
            combine(getCurrentUser(), observePlanActive.activeAt(workplaceId)) { user, planActive -> user to planActive }
                .distinctUntilChanged()
                .collect { (user, planActive) -> load(user, planActive) }
        }
    }

    private suspend fun load(user: User?, planActive: Boolean) {
        viewer = user
        if (user == null) {
            _uiState.value = EmployeeTimelineUiState(isLoading = false, notAvailable = true)
            return
        }
        timelines.load(workplaceId, userId ?: user.uid, user, planActive)
            .onSuccess { view ->
                _uiState.update { it.copy(isLoading = false, view = view, notAvailable = false) }
                loadRequests()
            }
            .onFailure { _uiState.update { it.copy(isLoading = false, notAvailable = it.view == null) } }
    }

    // --- SH-224: the person's requests -------------------------------------------------------------

    /** Reads the person's requests for the axis' days (one query per collection), when the axis changed or [force]d. */
    private fun loadRequests(force: Boolean = false) {
        val use = requests ?: return
        val view = _uiState.value.view ?: return
        val axis = view.timeline.axis
        if (!force && axis == loadedAxis) return
        loadedAxis = axis
        requestsJob.launch(viewModelScope) {
            use.load(workplaceId, view.timeline.userId, axis)
                .onSuccess { found -> _uiState.update { it.copy(requests = found.copy(showDeclined = it.requests?.showDeclined == true)) } }
                .onFailure { e ->
                    loadedAxis = null
                    _uiState.update { it.copy(error = e.toUiText()) }
                }
        }
    }

    /** "Pokaż odrzucone": rejected requests drawn too, or hidden again (no new read: they were read). */
    fun toggleDeclined() = _uiState.update { s -> s.copy(requests = s.requests?.let { it.copy(showDeclined = !it.showDeclined) }) }

    /** Opens SH-223's new-request form for the person, starting on [lane]'s type. */
    fun startNewRequest(lane: RequestLane? = null) {
        val use = requests ?: return
        val state = _uiState.value
        val view = state.view ?: return
        if (!state.canAddRequests) return
        val loaded = state.requests ?: TimelineRequests()
        val form = AbsencesUiState(
            userId = view.timeline.userId,
            workplaceId = workplaceId,
            activeAbsences = loaded.requests.mapNotNull { it.absence },
            workRequests = loaded.requests.mapNotNull { it.work },
            requestSettings = view.requestSettings,
            market = view.market,
            financial = view.financial
        )
        val forName = view.personName.takeUnless { view.isMine }
        _uiState.update {
            it.copy(
                newRequest = TimelineNewRequest(form, lane?.initialType(), forName, canApproveNow = forName != null && it.canDecideRequests),
                detail = null
            )
        }
        viewModelScope.launch {
            use.places(workplaceId).onSuccess { places -> updateForm { it.copy(places = places) } }
        }
    }

    private fun updateForm(change: (AbsencesUiState) -> AbsencesUiState) =
        _uiState.update { s -> s.newRequest?.let { s.copy(newRequest = it.copy(form = change(it.form))) } ?: s }

    fun dismissNewRequest() = _uiState.update { it.copy(newRequest = null) }

    /** A new place on the workplace's list, from the form (chosen there once added). */
    fun addPlace(name: String) {
        val use = requests ?: return
        val by = viewer ?: return
        val existing = _uiState.value.newRequest?.form?.places ?: return
        viewModelScope.launch {
            use.addPlace(workplaceId, name, by, existing).fold(
                onSuccess = { place -> updateForm { f -> f.copy(places = if (f.places.any { it.id == place.id }) f.places else f.places + place, addedPlace = place) } },
                onFailure = { e -> _uiState.update { it.copy(error = e.toUiText()) } }
            )
        }
    }

    fun addedPlaceConsumed() = updateForm { it.copy(addedPlace = null) }

    /** Sends the form's request for the person (PENDING, SH-223's checks); shown at once, outlined. */
    fun submitNewRequest(request: NewRequest) {
        val use = requests ?: return
        val by = viewer ?: return
        val state = _uiState.value
        val view = state.view ?: return
        if (!state.canAddRequests || state.requestSaving) return
        val person = view.timeline.userId
        val before = state.requests ?: TimelineRequests()
        // "Approve now" only for whoever decides requests, and only for someone else's.
        val approveNow = state.canDecideRequests && !view.isMine && when (request) {
            is NewRequest.Leave -> request.approveNow
            is NewRequest.Work -> request.approveNow
        }
        val shownStatus = if (approveNow) AbsenceStatus.APPROVED else AbsenceStatus.PENDING
        val shown = when (request) {
            is NewRequest.Leave -> TimelineRequest.of(request.draft.copy(id = "$PENDING_ID_PREFIX${request.draft.startDate}", userId = person, workplaceId = workplaceId, status = shownStatus))
            is NewRequest.Work -> TimelineRequest.of(request.draft.copy(id = "$PENDING_ID_PREFIX${request.draft.startDate}", userId = person, workplaceId = workplaceId, status = shownStatus))
        }
        _uiState.update { it.copy(newRequest = null, requestSaving = true, requests = before.copy(requests = before.requests + listOfNotNull(shown))) }
        viewModelScope.launch {
            val result = when (request) {
                is NewRequest.Leave -> use.create(request.draft.copy(userId = person, workplaceId = workplaceId), by, approveNow)
                is NewRequest.Work -> use.create(request.draft.copy(userId = person, workplaceId = workplaceId), by, approveNow)
            }
            result.fold(
                onSuccess = {
                    val message = if (approveNow) Res.string.timeline_request_approved else Res.string.timeline_request_sent
                    _uiState.update { it.copy(requestSaving = false, message = UiText.Resource(message)) }
                    loadRequests(force = true)
                },
                onFailure = { e -> _uiState.update { it.copy(requestSaving = false, requests = rolledBack(it.requests, before), error = e.toUiText()) } }
            )
        }
    }

    /** Keeps the "show rejected" choice made meanwhile. */
    private fun rolledBack(now: TimelineRequests?, before: TimelineRequests): TimelineRequests =
        before.copy(showDeclined = now?.showDeclined ?: before.showDeclined)

    /** Opens [key]'s detail (everyone: the person reads their own; the decisions are the managers'). */
    fun openRequest(key: String) {
        if (_uiState.value.requests?.find(key) == null) return
        _uiState.update { it.copy(detail = TimelineRequestDetail(key)) }
    }

    fun closeRequest() = _uiState.update { it.copy(detail = null) }

    /** Approves the open pending request; over a yearly limit it asks for the note first. */
    fun approveRequest() = decide(AbsenceStatus.APPROVED)

    fun askRejectRequest() = updateDetail { it.copy(rejecting = true) }

    fun cancelRejectRequest() = updateDetail { it.copy(rejecting = false) }

    fun confirmRejectRequest(reason: String) = decide(AbsenceStatus.REJECTED, rejectionReason = reason.asRejectionReason())

    /** Approves over the limit with the manager's [note] (SH-223: stored with the request). */
    fun approveOverLimit(note: String) {
        if (note.isBlank()) return
        decide(AbsenceStatus.APPROVED, overrideNote = note.trim())
    }

    fun dismissOverLimit() = updateDetail { it.copy(overLimit = null) }

    fun askWithdrawRequest() = updateDetail { it.copy(withdrawing = true) }

    fun cancelWithdrawRequest() = updateDetail { it.copy(withdrawing = false) }

    private fun updateDetail(change: (TimelineRequestDetail) -> TimelineRequestDetail) =
        _uiState.update { s -> s.detail?.let { s.copy(detail = change(it)) } ?: s }

    private fun decide(status: AbsenceStatus, rejectionReason: String? = null, overrideNote: String? = null) {
        val use = requests ?: return
        val by = viewer ?: return
        val state = _uiState.value
        val request = state.openRequest ?: return
        if (!state.canDecideRequests || state.requestSaving || request.status != AbsenceStatus.PENDING || request.id.isPending) return
        val before = state.requests ?: return
        _uiState.update { it.copy(requestSaving = true, detail = null, requests = before.withStatus(request.key, status)) }
        viewModelScope.launch {
            use.decide(request, status, by, rejectionReason, overrideNote).fold(
                onSuccess = { decision ->
                    if (decision is AbsenceDecision.OverLimit) {
                        _uiState.update {
                            it.copy(requestSaving = false, requests = rolledBack(it.requests, before), detail = TimelineRequestDetail(request.key, overLimit = decision.check))
                        }
                    } else {
                        val message = if (status == AbsenceStatus.APPROVED) Res.string.timeline_request_approved else Res.string.timeline_request_rejected
                        _uiState.update { it.copy(requestSaving = false, message = UiText.Resource(message)) }
                        loadRequests(force = true)
                    }
                },
                onFailure = { e ->
                    val cause = (e as? ResolveConflictsFailedException)?.cause
                    val text = if (cause != null) UiText.Resource(Res.string.requests_resolve_failed, cause.toUiText()) else e.toUiText()
                    _uiState.update { it.copy(requestSaving = false, requests = rolledBack(it.requests, before), error = text) }
                }
            )
        }
    }

    /** Withdraws the open request (deleted, as SH-223 withdraws one). */
    fun confirmWithdrawRequest() {
        val use = requests ?: return
        val state = _uiState.value
        val request = state.openRequest ?: return
        if (!state.canDecideRequests || state.requestSaving || request.id.isPending) return
        val before = state.requests ?: return
        _uiState.update { it.copy(requestSaving = true, detail = null, requests = before.copy(requests = before.requests.filter { r -> r.key != request.key })) }
        viewModelScope.launch {
            use.withdraw(request).fold(
                onSuccess = {
                    _uiState.update { it.copy(requestSaving = false, message = UiText.Resource(Res.string.timeline_request_withdrawn)) }
                    loadRequests(force = true)
                },
                onFailure = { e -> _uiState.update { it.copy(requestSaving = false, requests = rolledBack(it.requests, before), error = e.toUiText()) } }
            )
        }
    }

    private fun TimelineRequests.withStatus(key: String, status: AbsenceStatus): TimelineRequests = copy(
        requests = requests.map { r ->
            if (r.key != key) r
            else r.copy(status = status, absence = r.absence?.copy(status = status), work = r.work?.copy(status = status))
        }
    )

    /** Opens the dialog for a new entry of [lane]. */
    fun startAdd(lane: TimelineLane) {
        val view = _uiState.value.view ?: return
        if (!view.canEdit) return
        _uiState.update { it.copy(draft = TimelineDraft.new(lane, view.timeline.today), draftProblem = null) }
    }

    /** Opens the dialog to change (or remove) [entry]; read-only viewers get nothing. */
    fun startEdit(entry: TimelineEntry) {
        if (!_uiState.value.canEdit) return
        _uiState.update { it.copy(draft = TimelineDraft.of(entry), draftProblem = null) }
    }

    fun updateDraft(change: (TimelineDraft) -> TimelineDraft) =
        _uiState.update { s -> s.draft?.let { s.copy(draft = change(it), draftProblem = null) } ?: s }

    fun dismissDraft() = _uiState.update { it.copy(draft = null, draftProblem = null) }

    /** Saves the dialog's entry: added, or replacing the one it changes. */
    fun saveDraft() {
        val draft = _uiState.value.draft ?: return
        val entry = draft.toEntry() ?: return
        val edit = draft.editingId?.let { TimelineEdit.Replace(it, entry) } ?: TimelineEdit.Add(entry)
        submit(edit, fromDialog = true)
    }

    /** Asks before removing [entry] (from its dialog). */
    fun askRemove(entry: TimelineEntry) = _uiState.update { it.copy(removing = entry, draft = null, draftProblem = null) }

    fun cancelRemove() = _uiState.update { it.copy(removing = null) }

    fun confirmRemove() {
        val entry = _uiState.value.removing ?: return
        _uiState.update { it.copy(removing = null) }
        submit(TimelineEdit.Remove(entry.id), fromDialog = false)
    }

    /** A drag ended: [entryId] moves by [days] (start and end together; snapped to days by the screen). */
    fun move(entryId: String, days: Int) {
        if (days == 0) return
        val view = _uiState.value.view ?: return
        if (!view.canEdit) return
        val entry = view.timeline.entry(entryId) ?: return
        submit(TimelineEdit.Replace(entryId, entry.movedBy(days)), fromDialog = false)
    }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }

    private fun submit(edit: TimelineEdit, fromDialog: Boolean) {
        val state = _uiState.value
        val view = state.view ?: return
        val editor = viewer ?: return
        if (!view.canEdit || state.isSaving) return
        // The same checks as the save, at once: a refused change never shows as made.
        val removesHire = edit is TimelineEdit.Remove && edit.id == TimelineKind.HIRE.name
        val after = view.timeline.copy(entries = TimelineRules.apply(view.timeline, edit))
        val problem = TimelineRules.refusals(view.timeline, after, view.shifts, removesHire).firstOrNull()?.toUiText()
        if (problem != null) {
            _uiState.update { if (fromDialog) it.copy(draftProblem = problem) else it.copy(error = problem) }
            return
        }
        // Optimistic: the timeline shows the change while it is written.
        _uiState.update { it.copy(view = view.copy(timeline = after), draft = null, draftProblem = null, isSaving = true) }
        viewModelScope.launch {
            timelines.save(workplaceId, view.timeline.userId, edit, editor)
                .onSuccess { saved ->
                    val trigger = saved.recompute
                    val message = when {
                        trigger == null -> UiText.Resource(Res.string.timeline_saved)
                        trigger.from == null -> UiText.Resource(Res.string.timeline_saved_recompute_start)
                        else -> UiText.Resource(Res.string.timeline_saved_recompute, com.evoapps.evoshift.ui.user.formatDay(trigger.from!!))
                    }
                    _uiState.update { it.copy(view = saved.view, isSaving = false, message = message) }
                    loadRequests()
                    if (trigger != null) {
                        // SH-147: labour cost screens read again now and once the months are recalculated.
                        payTermsChanges?.changed()
                        saves.launch(repriceSaveKey(workplaceId), SaveKind.RECALCULATE, queue = true) {
                            timelines.recompute(workplaceId, trigger, saved.view.timeline.hire?.from).also { payTermsChanges?.changed() }
                        }
                    }
                }
                .onFailure { e ->
                    val text = (e as? InvalidTimelineException)?.problems?.firstOrNull()?.toUiText() ?: e.toUiText()
                    _uiState.update { it.copy(view = view, isSaving = false, error = text) }
                }
        }
    }
}
